import { CdkConnectedOverlay, CdkOverlayOrigin } from '@angular/cdk/overlay';
import { NgClass } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { Router } from '@angular/router';

import { MatCard, MatCardContent } from '@angular/material/card';
import { MatIcon } from '@angular/material/icon';
import { TranslatePipe } from '@ngx-translate/core';

import { TextSeparatorComponent } from '@lib/shared-ui/commons/text-separator/text-separator.component';

import { TASK_DATA } from '../../../core/contracts/task-data.contract';
import { HEALTH_CHECK_IN_RESULT_CHIP_CLASS } from '../../../core/models/health-check-in.const';
import { Task, TaskType } from '../../../core/models/task.interface';
import { MultilineTextComponent } from '../../commons/multiline-text/multiline-text.component';
import { TaskDataTargetComponent } from '../../commons/task-data-target/task-data-target.component';

@Component({
  selector: 'tasks-health-check-in-task-summary',
  standalone: true,
  imports: [
    NgClass,
    MatIcon,
    MatCard,
    MatCardContent,
    CdkOverlayOrigin,
    CdkConnectedOverlay,
    TranslatePipe,
    TextSeparatorComponent,
    TaskDataTargetComponent,
    MultilineTextComponent,
  ],
  template: `
    <div class='flex flex-col gap-1'>
      <tasks-task-data-target (click)='navigateToMemberProfile()'>
        <mat-icon icon>account_circle</mat-icon>
        <span text>{{ task.data.memberName }}</span>
      </tasks-task-data-target>
      <div class='flex min-w-0 items-center gap-2'>
        <span
          class='shrink-0 rounded-full px-2 py-0.5 text-xs'
          [ngClass]='resultChipClass'
        >
          {{ 'task.label.healthCheckInResult' + task.data.result | translate }}
        </span>
        @if (task.data.comment) {
          <shared-text-separator class='shrink-0' />
          <p
            cdkOverlayOrigin
            #trigger="cdkOverlayOrigin"
            class='min-w-0 cursor-pointer truncate text-neutral-500 hover:text-neutral-600'
            [ngClass]="{ 'text-neutral-600': commentOpen() }"
            (click)="commentOpen.set(!commentOpen())"
          >{{ task.data.comment }}</p>
          <ng-template
            cdkConnectedOverlay
            [cdkConnectedOverlayOrigin]="trigger"
            [cdkConnectedOverlayOpen]="commentOpen()"
            [cdkConnectedOverlayHasBackdrop]="true"
            [cdkConnectedOverlayDisposeOnNavigation]="true"
            (backdropClick)="commentOpen.set(false)"
          >
            <mat-card class="w-96">
              <mat-card-content>
                <tasks-multiline-text [text]="task.data.comment" />
              </mat-card-content>
            </mat-card>
          </ng-template>
        }
      </div>
    </div>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HealthCheckInTaskSummaryComponent {
  protected readonly router = inject(Router);
  protected readonly task = inject<Task<TaskType.HealthCheckIn>>(TASK_DATA);

  protected readonly commentOpen = signal<boolean>(false);
  protected readonly resultChipClass = HEALTH_CHECK_IN_RESULT_CHIP_CLASS[this.task.data.result];

  protected navigateToMemberProfile(): void {
    this.router.navigate(['/members', this.task.data.memberId]);
  }
}
